/**
 * Editor dialog: lets the user reorder / add / remove supplier shortcut chips.
 * Order is drag-free here (use up/down arrows) to stay simple and dep-free.
 *
 * Extracted from OrdersTab.jsx for maintainability.
 */
import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export default function SupplierShortcutEditor({
    open,
    onClose,
    suppliers,
    shortcutNames,
    onChange,
    defaults,
    resolveShortcutSupplier,
}) {
    const [draft, setDraft] = useState([]);
    const [picker, setPicker] = useState('');

    useEffect(() => {
        if (open) {
            setDraft([...shortcutNames]);
            setPicker('');
        }
    }, [open, shortcutNames]);

    const addSupplier = (supplierId) => {
        const sup = suppliers.find(s => s.id === supplierId);
        if (!sup) return;
        if (draft.some(n => resolveShortcutSupplier(n)?.id === supplierId)) {
            toast.error('Ce fournisseur est déjà dans les raccourcis');
            return;
        }
        setDraft(prev => [...prev, sup.name]);
        setPicker('');
    };

    const removeAt = (i) => setDraft(prev => prev.filter((_, idx) => idx !== i));
    const moveAt = (i, delta) => {
        setDraft(prev => {
            const copy = [...prev];
            const j = i + delta;
            if (j < 0 || j >= copy.length) return prev;
            [copy[i], copy[j]] = [copy[j], copy[i]];
            return copy;
        });
    };

    const handleSave = () => {
        onChange(draft);
        toast.success('Raccourcis fournisseurs mis à jour');
        onClose();
    };

    const handleResetDefaults = () => {
        setDraft([...defaults]);
    };

    // Suppliers not yet in the list
    const remainingSuppliers = suppliers
        .filter(s => !draft.some(n => resolveShortcutSupplier(n)?.id === s.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="supplier-shortcut-editor">
                <DialogHeader>
                    <DialogTitle>Raccourcis fournisseurs</DialogTitle>
                    <DialogDescription>
                        Personnalisez la barre de raccourcis de la page Commandes. Ordre modifiable, retrait et ajout depuis la liste complète.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {draft.length === 0 && (
                        <div className="text-center text-xs text-slate-400 py-6">Aucun raccourci. Ajoutez-en un ci-dessous.</div>
                    )}
                    {draft.map((name, i) => {
                        const sup = resolveShortcutSupplier(name);
                        return (
                            <div key={`${name}-${i}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
                                <span className="flex-1 text-sm text-slate-700">
                                    {sup?.name || (
                                        <span className="text-red-500 italic">{name} (introuvable)</span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => moveAt(i, -1)}
                                    disabled={i === 0}
                                    className="text-slate-500 hover:text-orange-600 disabled:opacity-30 text-sm px-1"
                                    title="Monter"
                                >▲</button>
                                <button
                                    type="button"
                                    onClick={() => moveAt(i, 1)}
                                    disabled={i === draft.length - 1}
                                    className="text-slate-500 hover:text-orange-600 disabled:opacity-30 text-sm px-1"
                                    title="Descendre"
                                >▼</button>
                                <button
                                    type="button"
                                    onClick={() => removeAt(i)}
                                    className="text-red-400 hover:text-red-600"
                                    title="Retirer"
                                    data-testid={`remove-shortcut-${i}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="pt-2 border-t border-slate-100">
                    <Select value={picker} onValueChange={addSupplier}>
                        <SelectTrigger data-testid="add-shortcut-select" className="w-full">
                            <SelectValue placeholder="Ajouter un fournisseur..." />
                        </SelectTrigger>
                        <SelectContent>
                            {remainingSuppliers.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                            {remainingSuppliers.length === 0 && (
                                <div className="px-2 py-1 text-xs text-slate-400">Tous les fournisseurs sont déjà présents.</div>
                            )}
                        </SelectContent>
                    </Select>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={handleResetDefaults} data-testid="reset-defaults-btn">
                        Réinitialiser
                    </Button>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-shortcuts-btn">
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
